import { useCallback, useEffect, useRef } from "react";
import { useBeforeUnload, useLocation } from "react-router";

import { isPreviewing } from "@builder.io/sdk-react";

import { isLearningCenterPath } from "~/hooks/learning-center/use-is-learning-center";
import useLearningCenter from "~/hooks/use-learning-center";
import { useRootLoaderData } from "~/root";

import { getDeviceType, trackSegment } from "./utils";

/**
 * Learning Center navigation tracking — Segment "Entered/Exited Learning Center"
 * events (HSA only). Port of hec-composable
 * `overrides/app/utils/segment/learning-center/LearningCenterNavTracker.js`
 * (ANALYTICS-39).
 *
 * This is a HOOK, not a component: it renders nothing and is pure client-side
 * route tracking. Call it once near the app root (alongside the other
 * location-watchers in `App()`). It does NOT need `SiteProvider` — site id +
 * auth come from the root loader (`useRootLoaderData`).
 *
 * Why not route middleware: RR7 client middleware runs only on client
 * navigations + fetcher calls (NOT initial hydration → would miss enter on a
 * direct/hard load), and `beforeunload` is not a middleware concern — RR7
 * exposes it as the `useBeforeUnload` hook (used below). So the nav-transition
 * enter/exit lives in a `useLocation` effect and the unload exit in
 * `useBeforeUnload`; together they cover all four cases in one place.
 *
 * The audit/ticket reference "SFRA LearningCenterNavTracker" — that does not
 * exist; SFRA fires no LC nav events. The composable is the only legacy
 * implementation, and the live HSA payload was verified against it:
 *
 *   track("Entered Learning Center", { entryPageUrl, entryTimeStamp, device })
 *   track("Exited Learning Center",  { exitPageUrl,  exitTimeStamp,  device })
 *
 * There is no `duration` property on the wire (the ticket AC's "duration since
 * enter" is derived downstream from the entry/exit timestamp pair).
 *
 * Behaviour (matches the composable algorithm):
 *  - Enter fires on mount-while-in-LC and on a non-LC → LC transition. The
 *    prev-path transition check dedups re-renders / intra-LC navigation.
 *  - Exit fires on an LC → non-LC transition and on unload while in LC.
 *    Suppressed for engaged learners (`isRegistered && activeGoal`) — fires for
 *    anonymous users and registered users without an active goal.
 *  - Intra-LC SPA navigation (article → article) fires nothing.
 *  - LC = the pathname has a "learning-center" segment.
 *
 * KNOWN SEAM (Workers↔composable hybrid): in the Workers target only
 * `/learning-center/articles*` is served here; the LC landing and
 * `/learning-center/questionnaire` stay on the composable/MRT stack (see
 * audit_articles.md:45). Crossing that boundary is a hard navigation, so each
 * hop emits a spurious exit+enter pair (each app fires its own tracker) and one
 * logical LC visit fragments into multiple enter/exit segments. This is a
 * deliberate-scope consequence of the migration, not fixable from the Workers
 * side alone; it shrinks once the article hub (ARTICLES-03) lands and closes
 * fully only if the LC landing/quiz also move to Workers. Tracked under epic
 * SFCC-11239 (Articles/LC migration) + ANALYTICS-39 audit note.
 *
 * The `learning-center` path predicate is shared via
 * `~/hooks/learning-center/use-is-learning-center` (SFCC-11508) so the TPA
 * consent-modal guard and this tracker can't drift apart.
 */
export function useLearningCenterNavTracker(): void {
  const location = useLocation();
  const { site, isRegistered } = useRootLoaderData();
  const { learningCenterData } = useLearningCenter();
  // activeGoal is undefined until useLearningCenter is implemented (ARTICLES-11);
  // until then the suppression branch never trips, so exit fires for everyone —
  // matching today's anonymous-heavy LC traffic. Auto-corrects when the hook lands.
  const activeGoal = learningCenterData?.activeGoal;

  // HSA-only. FSA `/learning-center/*` proxies to SFRA and WDH has no LC, so
  // those never reach this route — but guard defensively per the ticket.
  const enabled = site.id === "HSASTORE";

  const prevPathRef = useRef<string | null>(null);

  const fireEnter = useCallback((path: string) => {
    void trackSegment("Entered Learning Center", {
      entryPageUrl: `${window.location.origin}${path}`,
      entryTimeStamp: new Date().toISOString(),
      device: getDeviceType(),
    });
  }, []);

  const fireExit = useCallback(
    (path: string) => {
      // Composable parity: engaged learners (registered with an active goal) are
      // treated as persisting in LC and don't emit an exit.
      if (isRegistered && activeGoal) return;
      void trackSegment("Exited Learning Center", {
        exitPageUrl: `${window.location.origin}${path}`,
        exitTimeStamp: new Date().toISOString(),
        device: getDeviceType(),
      });
    },
    [isRegistered, activeGoal],
  );

  // Enter/exit on navigation: mount-while-in-LC, and non-LC ↔ LC transitions.
  useEffect(() => {
    if (!enabled || isPreviewing()) return;

    const path = location.pathname;
    const isLC = isLearningCenterPath(path);
    const prev = prevPathRef.current;

    if (prev === null) {
      // Initial mount: fire enter if we land directly inside the LC section.
      if (isLC) fireEnter(path);
    } else if (prev !== path) {
      const wasLC = isLearningCenterPath(prev);
      // Exit reports the LC page being left, not the destination.
      if (!isLC && wasLC) fireExit(prev);
      if (isLC && !wasLC) fireEnter(path);
    }
    prevPathRef.current = path;
  }, [location.pathname, enabled, fireEnter, fireExit]);

  // Exit on hard navigation / tab close / refresh while inside LC. RR7-native
  // unload hook (middleware can't see beforeunload). Use the router location —
  // it's the page being left at unload time (matches the composable) and, unlike
  // window.location, is the value under test.
  useBeforeUnload(
    useCallback(() => {
      if (!enabled || isPreviewing()) return;
      if (isLearningCenterPath(location.pathname)) {
        fireExit(location.pathname);
      }
    }, [enabled, fireExit, location.pathname]),
  );
}
